import React, { useContext, useEffect, useReducer, useState, useCallback, useRef } from "react";
import { Link as RouterLink, useLocation, useHistory } from "react-router-dom";
import clsx from "clsx";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import Avatar from "@material-ui/core/Avatar";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";

import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnOutlinedIcon from "@material-ui/icons/FlashOnOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import ViewKanban from "@mui/icons-material/ViewKanban";
import Schedule from "@material-ui/icons/Schedule";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import TrackChangesOutlinedIcon from "@material-ui/icons/TrackChangesOutlined";
import EventIcon from "@material-ui/icons/Event";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import BusinessIcon from "@material-ui/icons/Business";
import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import SettingsApplications from "@material-ui/icons/SettingsApplications";
import ReceiptIcon from "@material-ui/icons/Receipt";
import StoreIcon from "@material-ui/icons/Store";
import {
  AllInclusive,
  AttachFile,
  Dashboard,
  Description,
  DeviceHubOutlined,
  GridOn,
  ListAlt,
  PhonelinkSetup,
  ShoppingCart,
  TrendingUp,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  DirectionsCar as CarIcon,
  EventAvailable as EventAvailableIcon
} from "@material-ui/icons";

import SignalCellularConnectedNoInternet4BarIcon from "@material-ui/icons/SignalCellularConnectedNoInternet4Bar";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";

// Import do submodulo, nao do pacote: `from "lodash"` arrasta o lodash
// inteiro (101 KB) para o main.js so para usar isArray.
import isArray from "lodash/isArray";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";
import { Campaign, ShapeLine, Webhook, SmartToyOutlined as SmartToyOutlinedIcon } from "@mui/icons-material";
// Import direto do hook, NAO do barrel modules/consortium: o barrel importa
// as 5 paginas do modulo, e a de Dashboard puxa xlsx (408 KB) + recharts
// (227 KB) para dentro do main.js. Como a sidebar monta em toda tela, isso
// anulava o lazy load que o routes/index.js ja fazia dessas paginas.
import { useConsortiumModule } from "../modules/consortium/hooks/useConsortiumModule";

// Cores dos ícones
const iconStyles = {
  active: { color: "#FFFFFF" },
  inactive: { color: "rgba(255, 255, 255, 0.70)" },
};

// Contexto usado para avisar o Layout que o usuário está interagindo com o
// sidebar (clique em um item ou em um grupo de submenu). Isso reforça o
// estado de "mouse sobre o sidebar" e cancela qualquer fechamento automático
// pendente, mesmo que o clique dispare um reflow que gere um mouseleave
// espúrio no Drawer.
const SidebarInteractionContext = React.createContext(null);

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100%",
    userSelect: "none",
    margin: 0,
    padding: 0,
  },

  listItem: {
    minHeight: "28px",
    height: "28px",
    padding: "0 8px",
    margin: 0,
    width: "100%",
    borderRadius: 0,
    borderLeft: "3px solid transparent",
    transition: "background-color 120ms ease, border-color 120ms ease",
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
    },
  },

  listItemSmall: {
    minHeight: "25px",
    height: "25px",
    padding: "0 8px 0 24px",
    margin: 0,
    width: "100%",
    borderRadius: 0,
    borderLeft: "3px solid transparent",
    transition: "background-color 120ms ease, border-color 120ms ease",
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
    },
  },

  listItemActive: {
    background: "rgba(255,255,255,0.16) !important",
    borderLeft: "3px solid #38bdf8 !important",
    "& $listItemText": {
      fontWeight: 600,
      color: "#FFFFFF",
    },
    "& $iconHoverActive": {
      color: "#FFFFFF",
    },
  },

  // Grupo de menus: conecta visualmente o Menu Pai aos Submenus
  menuGroup: {
    width: "100%",
    borderLeft: "3px solid transparent",
    transition: "background-color 150ms ease, border-color 150ms ease",
  },

  menuGroupOpen: {
    background: "rgba(255, 255, 255, 0.04)",
    borderLeft: "3px solid #38bdf8",
  },

  groupParentActive: {
    background: "rgba(255, 255, 255, 0.09) !important",
    "& $listItemText": {
      fontWeight: 600,
      color: "#FFFFFF",
    },
  },

  listItemText: {
    fontSize: "12px",
    fontWeight: 500,
    color: "rgba(255,255,255,0.85)",
    letterSpacing: "0.01em",
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  iconHoverActive: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0,
    height: 20,
    width: 20,
    backgroundColor: "transparent",
    color: "rgba(255, 255, 255, 0.70)",
    transition: "color 0.15s ease",
    "&.active": {
      color: "#FFFFFF",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1.05rem",
    },
  },

  versionChip: {
    background: "rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.60)",
    fontWeight: 600,
    fontSize: "0.62rem",
    height: 18,
    borderRadius: 0,
  },

  subheader: {
    color: "rgba(255,255,255,0.35)",
    fontSize: "0.58rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "1.2px",
    padding: "2px 8px",
    lineHeight: "18px",
    minHeight: "18px",
  },

  expandIcon: {
    color: "rgba(255,255,255,0.45)",
    fontSize: "0.95rem",
  },

  collapsedList: {
    "& .MuiListItem-root": {
      justifyContent: "center",
      paddingLeft: 0,
      paddingRight: 0,
    },
    "& .MuiListItemIcon-root": {
      minWidth: "auto",
      justifyContent: "center",
      margin: 0,
    },
    "& .MuiListItemText-root": {
      display: "none",
    }
  },
}));

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, iconKey, small, onClick, closeSubmenus } = props;
  const classes = useStyles();
  const theme = useTheme();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const onMenuInteract = useContext(SidebarInteractionContext);
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const handleClick = (e) => {
    if (typeof onMenuInteract === "function") {
      onMenuInteract();
    }
    if (!small && typeof closeSubmenus === "function") {
      closeSubmenus();
    }
    if (onClick) {
      onClick(e);
    }
  };

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip
        placement="right"
        arrow
        title={
          <Typography style={{ fontWeight: 700, fontSize: "0.85rem" }}>
            {primary}
          </Typography>
        }
      >
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <ConditionalTooltip tooltipEnabled={!!tooltip}>
      <li>
        <ListItem
          button
          component={renderLink}
          className={clsx(
            small ? classes.listItemSmall : classes.listItem,
            isActive && `${classes.listItemActive} sidebar-active-item`
          )}
          style={tooltip ? { paddingLeft: 0, justifyContent: "center" } : undefined}
          onClick={handleClick}
        >
          {icon ? (
            <ListItemIcon style={tooltip ? { minWidth: "auto" } : { minWidth: small ? "22px" : "26px" }}>
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  <Avatar
                    className={clsx(classes.iconHoverActive, isActive && "active")}
                    style={{
                      color: isActive ? iconStyles.active.color : iconStyles.inactive.color,
                      backgroundColor: "transparent"
                    }}
                  >
                    {icon}
                  </Avatar>
                </Badge>
              ) : (
                <Avatar
                  className={clsx(classes.iconHoverActive, isActive && "active")}
                  style={{
                    color: isActive ? iconStyles.active.color : iconStyles.inactive.color,
                    backgroundColor: "transparent"
                  }}
                >
                  {icon}
                </Avatar>
              )}
            </ListItemIcon>
          ) : null}
          <ListItemText
            primary={
              <Typography className={classes.listItemText}>
                {primary}
              </Typography>
            }
          />
        </ListItem>
      </li>
    </ConditionalTooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose, onMenuInteract }) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  
  // Acordeão Exclusivo: Apenas 1 grupo aberto por vez
  const [openedSubmenu, setOpenedSubmenu] = useState(null);

  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showFinanceiro, setShowFinanceiro] = useState(false);
  const { hasConsortium } = useConsortiumModule();

  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showGoogleCalendar, setShowGoogleCalendar] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);
  const [showAiSdr, setShowAiSdr] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function checkHelps() {
      if (!user.id) return;
      try {
        const helps = await list();
        if (isMounted) setHasHelps(helps.length > 0);
      } catch (err) {
        console.log("Erro ao buscar ajuda (provavelmente deslogado)");
      }
    }
    checkHelps();
    return () => {
      isMounted = false;
    };
  }, [user]);

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config");

  const isIntegrationsRouteActive =
    location.pathname === "/departments" ||
    location.pathname === "/queues" ||
    location.pathname === "/prompts" ||
    location.pathname === "/ai-profile" ||
    location.pathname === "/queue-integration" ||
    location.pathname.startsWith("/flowbuilders") ||
    location.pathname.startsWith("/flowbuilder") ||
    location.pathname === "/integrations/google-calendar";

  const isFinanceiroRouteActive =
    location.pathname === "/financeiro";

  const isVendasRouteActive =
    location.pathname === "/products" ||
    location.pathname === "/services" ||
    location.pathname === "/budgets" ||
    location.pathname === "/proposals";

  const isConsortiumRouteActive =
    location.pathname.startsWith("/consortium");

  const isMechanicRouteActive =
    location.pathname.startsWith("/mechanic-") ||
    location.pathname === "/mechanics";

  const openMechanicSubmenu = openedSubmenu === "mechanic";
  const openIntegrationsSubmenu = openedSubmenu === "integrations";
  const openCampaignSubmenu = openedSubmenu === "campaign";
  const openFinanceiroSubmenu = openedSubmenu === "financeiro";
  const openConsortiumSubmenu = openedSubmenu === "consortium";

  // Lógica de acordeão exclusivo: abrir um grupo fecha todos os outros
  const toggleExclusiveSubmenu = (key) => {
    if (typeof onMenuInteract === "function") {
      onMenuInteract();
    }
    setOpenedSubmenu((prev) => (prev === key ? null : key));
  };

  const closeAllSubmenus = () => {
    setOpenedSubmenu(null);
  };

  // Sincronização automática com a rota ativa: fecha submenus se navegar para qualquer outro menu
  useEffect(() => {
    if (isIntegrationsRouteActive) setOpenedSubmenu("integrations");
    else if (isCampaignRouteActive) setOpenedSubmenu("campaign");
    else if (isConsortiumRouteActive) setOpenedSubmenu("consortium");
    else if (isFinanceiroRouteActive || isVendasRouteActive) setOpenedSubmenu("financeiro");
    else if (isMechanicRouteActive) setOpenedSubmenu("mechanic");
    else setOpenedSubmenu(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();
  const { getVersion } = useVersion();

  useEffect(() => {
    let isMounted = true;
    async function fetchVersion() {
      if (!user.id) return;
      try {
        const _version = await getVersion();
        if (isMounted) setVersion(_version.version);
      } catch (err) {
        console.log("Erro ao buscar versão (provavelmente deslogado)");
      }
    }
    fetchVersion();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      if (!user.companyId) return;
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!isMounted) return;

        setShowCampaigns(planConfigs.plan.useCampaigns);
        setShowKanban(planConfigs.plan.useKanban);
        setShowOpenAi(planConfigs.plan.useOpenAi);
        setShowIntegrations(planConfigs.plan.useIntegrations);
        setShowSchedules(planConfigs.plan.useSchedules);
        setShowInternalChat(planConfigs.plan.useInternalChat);
        setShowExternalApi(planConfigs.plan.useExternalApi);
        setShowGoogleCalendar(planConfigs.plan.useGoogleCalendar);
        setShowFinanceiro(planConfigs.plan.useFinanceiro);
        setShowCatalog(!!planConfigs.plan.useCatalog);
        setShowScheduling(!!planConfigs.plan.useScheduling);
        setShowAiSdr(!!planConfigs.plan.useAiSdr);
      } catch (err) {
        console.log("Erro ao buscar plano (provavelmente deslogado)");
      }
    }
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    const fetchChats = async () => {
      try {
        const { data } = await api.get("/chats/", {
          params: { searchParam, pageNumber },
        });
        if (isMounted) {
          dispatch({ type: "LOAD_CHATS", payload: data.records });
        }
      } catch (err) {
        console.error("Erro ao carregar chats em background:", err);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id) {
      const companyId = user.companyId;
      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      socket.on(`company-${companyId}-chat`, onCompanyChatMainListItems);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChatMainListItems);
      };
    }
  }, [socket]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const [invisibleAnnouncement, setInvisibleAnnouncement] = useState(true);
  const navigate = useHistory();

  useEffect(() => {
    let isMounted = true;
    const fetchAnnouncements = async () => {
      try {
        const { data } = await api.get("/announcements/", {
          params: { pageNumber: 1 },
        });
        if (!isMounted) return;
        if (data.records.length > 0) {
          const lastSeen = localStorage.getItem("lastSeenAnnouncement");
          if (lastSeen !== String(data.records[0].id)) {
            setInvisibleAnnouncement(false);
          } else {
            setInvisibleAnnouncement(true);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar anúncios em background:", err);
      }
    };

    if (user.companyId) {
      fetchAnnouncements();
    }

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    if (user.companyId) {
      const onCompanyAnnouncement = (data) => {
        if (data.action === "update" || data.action === "create") {
          setInvisibleAnnouncement(false);
        }
      };
      socket.on(`company-announcement`, onCompanyAnnouncement);

      return () => {
        socket.off(`company-announcement`, onCompanyAnnouncement);
      };
    }
  }, [user]);

  return (
    <SidebarInteractionContext.Provider value={onMenuInteract}>
    <div
      onMouseEnter={onMenuInteract}
      onMouseMove={onMenuInteract}
      onClick={isMobile ? drawerClose : undefined}
      className={clsx(classes.rootContainer, collapsed && classes.collapsedList)}
    >
      {!isMobile && (user.profile === "admin" || user.profile === "supervisor" || user.showDashboard === "enabled") && (
        <ListItemLink
          to="/"
          primary="Dashboard"
          icon={<DashboardOutlinedIcon />}
          iconKey="dashboard"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}
      {(user.whatsappIds?.length > 0 || user.whatsapp_id) && (
        <ListItemLink
          to="/tickets"
          primary={i18n.t("mainDrawer.listItems.tickets")}
          icon={<WhatsAppIcon />}
          iconKey="tickets"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}
      {showKanban && (user.profile === "admin" || user.profile === "super_admin" || user.super || user.allowKanban === "enabled") && (
        <ListItemLink
          to="/kanban"
          primary={i18n.t("mainDrawer.listItems.kanban")}
          icon={<AccountTreeOutlinedIcon />}
          iconKey="kanban"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {!isMobile && (
        <ListItemLink
          to="/contacts"
          primary="Clientes"
          icon={<ContactPhoneOutlinedIcon />}
          iconKey="contacts"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {!isMobile && user.company?.businessType === "mechanic" && (
        <div className={clsx(classes.menuGroup, openMechanicSubmenu && classes.menuGroupOpen)}>
          <ListItem
            button
            className={clsx(
              classes.listItem,
              (openMechanicSubmenu || isMechanicRouteActive) && classes.groupParentActive
            )}
            onClick={() => toggleExclusiveSubmenu("mechanic")}
            style={collapsed ? { paddingLeft: "0", justifyContent: "center" } : undefined}
          >
            <ListItemIcon style={collapsed ? { minWidth: "auto" } : { minWidth: "26px" }}>
              <Avatar
                className={clsx(classes.iconHoverActive, (isMechanicRouteActive || openMechanicSubmenu) && "active")}
                style={{
                  color: isMechanicRouteActive || openMechanicSubmenu ? iconStyles.active.color : iconStyles.inactive.color,
                  backgroundColor: "transparent"
                }}
              >
                <BuildIcon />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography className={classes.listItemText}>
                  Módulo Oficina
                </Typography>
              }
            />
            {!collapsed && (openMechanicSubmenu ? <ExpandLessIcon className={classes.expandIcon} /> : <ExpandMoreIcon className={classes.expandIcon} />)}
          </ListItem>
          <Collapse
            in={openMechanicSubmenu && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List component="div" disablePadding>
              <ListItemLink
                to="/mechanic-dashboard"
                primary="Painel Geral"
                icon={<DashboardOutlinedIcon />}
                tooltip={collapsed}
                small
              />
              <ListItemLink
                to="/mechanic-checklists"
                primary="Checklists"
                icon={<ListAlt />}
                tooltip={collapsed}
                small
              />
              <ListItemLink
                to="/mechanic-orders"
                primary="Ordens de Serviço"
                icon={<AssignmentIcon />}
                tooltip={collapsed}
                small
              />
              <ListItemLink
                to="/mechanics"
                primary="Equipe de Mecânicos"
                icon={<PeopleIcon />}
                tooltip={collapsed}
                small
              />
            </List>
          </Collapse>
        </div>
      )}

      {/* Atendente não gerencia Tags pelo menu */}
      {user.profile !== "user" && (
        <ListItemLink
          to="/tags"
          primary={i18n.t("mainDrawer.listItems.tags")}
          icon={<LocalOfferIcon />}
          iconKey="tags"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {!isMobile && user.company?.businessType === "consortium" && (
        <ListItemLink
          to="/consortium-crm"
          primary="Vendas Consórcio"
          icon={<TrendingUp />}
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {showSchedules && (
        <ListItemLink
          to="/schedules"
          primary={i18n.t("mainDrawer.listItems.schedules")}
          icon={<Schedule />}
          iconKey="schedules"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {showScheduling && (
        <ListItemLink
          to="/scheduling"
          primary="Agenda"
          icon={<EventIcon />}
          iconKey="scheduling"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {!isMobile && (
        <ListItemLink
          to="/quick-messages"
          primary={i18n.t("mainDrawer.listItems.quickMessages")}
          icon={<FlashOnOutlinedIcon />}
          iconKey="messages"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {showInternalChat && (
        <ListItemLink
          to="/chats"
          primary={i18n.t("mainDrawer.listItems.chats")}
          icon={
            <Badge
              color="secondary"
              variant="dot"
              invisible={invisible}
              overlap="circular"
            >
              <ForumIcon />
            </Badge>
          }
          iconKey="chats"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {!isMobile && hasHelps && (
        <ListItemLink
          to="/helps"
          primary={i18n.t("mainDrawer.listItems.helps")}
          icon={<HelpOutlineIcon />}
          iconKey="helps"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {!isMobile && (
      <>
      <Can
        role={
          user.profile === "user" &&
            user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        yes={() => (
          <>
            <Divider style={{ backgroundColor: "rgba(255, 255, 255, 0.08)", margin: "2px 0", display: collapsed ? "none" : "block" }} />
            <ListSubheader inset className={classes.subheader} disableSticky style={{ display: collapsed ? "none" : "block" }}>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            {showCatalog && !(user.company?.businessType === "medical" || user.company?.business_type === "medical" || user.company?.businessSpecialty === "Odontologia" || user.company?.business_specialty === "Odontologia") && (
              <ListItemLink
                to="/catalog"
                primary="Catálogo"
                icon={<StoreIcon />}
                iconKey="catalog"
                tooltip={collapsed}
                closeSubmenus={closeAllSubmenus}
              />
            )}


            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <div className={clsx(classes.menuGroup, openIntegrationsSubmenu && classes.menuGroupOpen)}>
                  <Tooltip
                    placement="right"
                    arrow
                    title={
                      collapsed ? (
                        <Typography style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {i18n.t("mainDrawer.listItems.queueIntegration")}
                        </Typography>
                      ) : (
                        ""
                      )
                    }
                  >
                    <ListItem
                      button
                      className={clsx(
                        classes.listItem,
                        (openIntegrationsSubmenu || isIntegrationsRouteActive) && classes.groupParentActive
                      )}
                      onClick={() => toggleExclusiveSubmenu("integrations")}
                      style={collapsed ? { paddingLeft: "0", justifyContent: "center" } : undefined}
                    >
                      <ListItemIcon style={collapsed ? { minWidth: "auto" } : { minWidth: "26px" }}>
                        <Avatar
                          className={clsx(classes.iconHoverActive, (isIntegrationsRouteActive || openIntegrationsSubmenu) && "active")}
                          style={{
                            color: isIntegrationsRouteActive || openIntegrationsSubmenu ? iconStyles.active.color : iconStyles.inactive.color,
                            backgroundColor: "transparent"
                          }}
                        >
                          <Webhook />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography className={classes.listItemText}>
                            {i18n.t("mainDrawer.listItems.queueIntegration")}
                          </Typography>
                        }
                      />
                      {!collapsed && (openIntegrationsSubmenu ? (
                        <ExpandLessIcon className={classes.expandIcon} />
                      ) : (
                        <ExpandMoreIcon className={classes.expandIcon} />
                      ))}
                    </ListItem>
                  </Tooltip>
                  <Collapse
                    in={openIntegrationsSubmenu && !collapsed}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {/* 1. Equipe */}
                      {user.profile !== "user" && (
                        <ListItemLink
                          small
                          to="/departments"
                          primary="Equipe"
                          icon={<PeopleAltOutlinedIcon />}
                          iconKey="departments"
                          tooltip={collapsed}
                        />
                      )}

                      {/* 2. Agente SDR */}
                      {showAiSdr && (
                        <ListItemLink
                          small
                          to="/ai-profile"
                          primary="Agente SDR"
                          icon={<SmartToyOutlinedIcon />}
                          iconKey="ai-profile"
                          tooltip={collapsed}
                        />
                      )}

                      {/* 3. Fluxo de Conversa */}
                      <ListItemLink
                        small
                        to="/flowbuilders"
                        primary="Fluxo de Conversa"
                        icon={<ShapeLine />}
                        iconKey="flowbuilder"
                        tooltip={collapsed}
                      />

                      {/* 4. Filas & Chatbot */}
                      <ListItemLink
                        small
                        to="/queues"
                        primary="Filas & Chatbot"
                        icon={<AccountTreeOutlinedIcon />}
                        iconKey="queues"
                        tooltip={collapsed}
                      />

                      {/* 5. Flowbuilder */}
                      {showIntegrations && (
                        <ListItemLink
                          small
                          to="/queue-integration"
                          primary="Flowbuilder"
                          icon={<DeviceHubOutlined />}
                          iconKey="integrations"
                          tooltip={collapsed}
                        />
                      )}

                      {/* 6. Talk AI */}
                      {showOpenAi && (
                        <ListItemLink
                          small
                          to="/prompts"
                          primary="Talk AI"
                          icon={<AllInclusive />}
                          iconKey="prompts"
                          tooltip={collapsed}
                        />
                      )}

                      {/* Google Agenda */}
                      {showGoogleCalendar && (
                        <ListItemLink
                          small
                          to="/integrations/google-calendar"
                          primary="Google Agenda"
                          icon={<EventIcon />}
                          iconKey="google-calendar"
                          tooltip={collapsed}
                        />
                      )}
                    </List>
                  </Collapse>
                </div>
              )}
            />

            {showCampaigns && (user.whatsappIds?.length > 0 || user.whatsapp_id) && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <div className={clsx(classes.menuGroup, openCampaignSubmenu && classes.menuGroupOpen)}>
                    <Tooltip
                      placement="right"
                      arrow
                      title={
                        collapsed ? (
                          <Typography
                            style={{
                              fontWeight: 700,
                              fontSize: "0.85rem",
                            }}
                          >
                            {i18n.t("mainDrawer.listItems.campaigns")}
                          </Typography>
                        ) : (
                          ""
                        )
                      }
                    >
                      <ListItem
                        button
                        className={clsx(
                          classes.listItem,
                          (openCampaignSubmenu || isCampaignRouteActive) && classes.groupParentActive
                        )}
                        onClick={() => toggleExclusiveSubmenu("campaign")}
                        style={collapsed ? { paddingLeft: "0", justifyContent: "center" } : undefined}
                      >
                        <ListItemIcon style={collapsed ? { minWidth: "auto" } : { minWidth: "26px" }}>
                          <Avatar
                            className={clsx(
                              classes.iconHoverActive,
                              (isCampaignRouteActive || openCampaignSubmenu) && "active"
                            )}
                            style={{
                              color: isCampaignRouteActive || openCampaignSubmenu ? iconStyles.active.color : iconStyles.inactive.color,
                              backgroundColor: "transparent"
                            }}
                          >
                            <EventAvailableIcon />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography className={classes.listItemText}>
                              {i18n.t("mainDrawer.listItems.campaigns")}
                            </Typography>
                          }
                        />
                        {!collapsed && (openCampaignSubmenu ? (
                          <ExpandLessIcon className={classes.expandIcon} />
                        ) : (
                          <ExpandMoreIcon className={classes.expandIcon} />
                        ))}
                      </ListItem>
                    </Tooltip>
                    <Collapse
                      in={openCampaignSubmenu && !collapsed}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List dense component="div" disablePadding>
                        <ListItemLink
                          small
                          to="/campaigns"
                          primary={i18n.t("campaigns.subMenus.list")}
                          icon={<ListIcon />}
                          iconKey="campaigns"
                          tooltip={collapsed}
                        />
                        <ListItemLink
                          small
                          to="/contact-lists"
                          primary={i18n.t("campaigns.subMenus.listContacts")}
                          icon={<PeopleIcon />}
                          iconKey="campaigns"
                          tooltip={collapsed}
                        />
                        <ListItemLink
                          small
                          to="/phrase-lists"
                          primary={i18n.t("flowbuilder.subMenus.campaign")}
                          icon={<EventAvailableIcon />}
                          iconKey="campaigns"
                          tooltip={collapsed}
                        />
                      </List>
                    </Collapse>
                  </div>
                )}
              />
            )}
          </>
        )}
      />

      {/* Financeiro ERP */}
      {!(user.company?.businessType === "medical" || user.company?.business_type === "medical" || user.company?.businessSpecialty === "Odontologia" || user.company?.business_specialty === "Odontologia") && showFinanceiro && (user.super || user.profile === "admin" || user.profile === "super_admin" || user.profile === "supervisor" || user.allowFinanceiroERP === "enabled") && (
        <div className={clsx(classes.menuGroup, openFinanceiroSubmenu && classes.menuGroupOpen)}>
          <Tooltip
            placement="right"
            arrow
            title={collapsed ? "Financeiro ERP" : ""}
          >
            <ListItem
              button
              className={clsx(
                classes.listItem,
                (openFinanceiroSubmenu || isFinanceiroRouteActive || isVendasRouteActive) && classes.groupParentActive
              )}
              onClick={() => toggleExclusiveSubmenu("financeiro")}
              style={collapsed ? { paddingLeft: "0", justifyContent: "center" } : undefined}
            >
              <ListItemIcon style={collapsed ? { minWidth: "auto" } : { minWidth: "26px" }}>
                <Avatar
                  className={clsx(
                    classes.iconHoverActive,
                    (isFinanceiroRouteActive || isVendasRouteActive || openFinanceiroSubmenu) && "active"
                  )}
                  style={{
                    color: isFinanceiroRouteActive || isVendasRouteActive || openFinanceiroSubmenu ? iconStyles.active.color : iconStyles.inactive.color,
                    backgroundColor: "transparent"
                  }}
                >
                  <AccountBalanceWalletOutlinedIcon />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.listItemText}>
                    Financeiro ERP
                  </Typography>
                }
              />
              {!collapsed && (openFinanceiroSubmenu ? (
                <ExpandLessIcon className={classes.expandIcon} />
              ) : (
                <ExpandMoreIcon className={classes.expandIcon} />
              ))}
            </ListItem>
          </Tooltip>
          <Collapse
            in={openFinanceiroSubmenu && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List dense component="div" disablePadding>
              {(user.super || user.profile === "admin" || user.profile === "super_admin") && (
                <ListItemLink
                  small
                  to="/financeiro"
                  primary="Fluxo de Caixa"
                  icon={<LocalAtmIcon />}
                  tooltip={collapsed}
                />
              )}
              <ListItemLink
                small
                to="/products"
                primary="Produtos"
                icon={<ShoppingCart />}
                tooltip={collapsed}
              />
              <ListItemLink
                small
                to="/services"
                primary="Serviços"
                icon={<ListAlt />}
                tooltip={collapsed}
              />
              <ListItemLink
                small
                to="/budgets"
                primary="Propostas"
                icon={<Description />}
                tooltip={collapsed}
              />
            </List>
          </Collapse>
        </div>
      )}

      {/* Módulo de Consórcio */}
      {hasConsortium && (
        <div className={clsx(classes.menuGroup, openConsortiumSubmenu && classes.menuGroupOpen)}>
          <Tooltip
            title={collapsed ? "Consórcio" : ""}
            placement="right"
            disableHoverListener={!collapsed}
          >
            <ListItem
              button
              className={clsx(
                classes.listItem,
                (openConsortiumSubmenu || isConsortiumRouteActive) && classes.groupParentActive
              )}
              onClick={() => toggleExclusiveSubmenu("consortium")}
              style={collapsed ? { paddingLeft: "0", justifyContent: "center" } : undefined}
            >
              <ListItemIcon style={collapsed ? { minWidth: "auto" } : { minWidth: "26px" }}>
                <Avatar
                  className={clsx(
                    classes.iconHoverActive,
                    (isConsortiumRouteActive || openConsortiumSubmenu) && "active"
                  )}
                  style={{
                    color: isConsortiumRouteActive || openConsortiumSubmenu ? iconStyles.active.color : iconStyles.inactive.color,
                    backgroundColor: "transparent"
                  }}
                >
                  <BusinessIcon />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography className={classes.listItemText}>
                    Consórcio
                  </Typography>
                }
              />
              {!collapsed && (openConsortiumSubmenu ? (
                <ExpandLessIcon className={classes.expandIcon} />
              ) : (
                <ExpandMoreIcon className={classes.expandIcon} />
              ))}
            </ListItem>
          </Tooltip>
          <Collapse
            in={openConsortiumSubmenu && !collapsed}
            timeout="auto"
            unmountOnExit
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                small
                to="/consortium"
                primary="Dashboard"
                icon={<Dashboard />}
                tooltip={collapsed}
              />
              <ListItemLink
                small
                to="/consortium/opportunities"
                primary="Oportunidades"
                icon={<Description />}
                tooltip={collapsed}
              />
              <ListItemLink
                small
                to="/consortium/cards"
                primary="Catálogo de Cartas"
                icon={<GridOn />}
                tooltip={collapsed}
              />
              <ListItemLink
                small
                to="/consortium/administrators"
                primary="Administradoras"
                icon={<StoreIcon />}
                tooltip={collapsed}
              />
              <ListItemLink
                small
                to="/consortium/types"
                primary="Tipos de Consórcio"
                icon={<ListAlt />}
                tooltip={collapsed}
              />
            </List>
          </Collapse>
        </div>
      )}

      {/* Configurações — admin/super */}
      {(user.super || user.profile === "admin" || user.profile === "super_admin") && (
        <ListItemLink
          to="/settings"
          primary={i18n.t("mainDrawer.listItems.settings")}
          icon={<SettingsOutlinedIcon />}
          iconKey="settings"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}
      {(user.super || user.role === "super_admin") && (
        <ListItemLink
          to="/global-config"
          primary="Configurações Globais"
          icon={<SettingsApplications />}
          iconKey="globalConfig"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}

      {/* Não-admin com permissão de conexões */}
      {user.profile !== "admin" &&
        user.profile !== "super_admin" &&
        !user.super &&
        user.allowConnections === "enabled" && (
        <ListItemLink
          to="/settings"
          primary={i18n.t("mainDrawer.listItems.settings")}
          icon={<SettingsOutlinedIcon />}
          iconKey="settings"
          tooltip={collapsed}
          closeSubmenus={closeAllSubmenus}
        />
      )}
      </>
      )}

      {!collapsed && (
        <React.Fragment>
          <Divider style={{ backgroundColor: "rgba(255, 255, 255, 0.08)", margin: "2px 0" }} />
          <Box style={{ padding: "3px 8px", textAlign: "center", marginTop: "auto" }}>
            <Chip
              label="V15.0.1"
              size="small"
              className={classes.versionChip}
            />
          </Box>
        </React.Fragment>
      )}
    </div>
    </SidebarInteractionContext.Provider>
  );
};

export default MainListItems;

